/**
 * The in-call "call an interpreter" request as the native call UI sees it.
 *
 * The native toolbar unmounts whenever it auto-hides, and the button may sit in
 * the More sheet, which closes on press. So the pending flag and the outcome
 * listeners live here rather than in a button, the way device handoff keeps its
 * progress outside the toolbar. Server contract: p2p_request_interpreter →
 * p2p_interpreter_requested, then p2p_interpreter_joined, p2p_interpreter_unavailable
 * or an error with operation p2p_request_interpreter.
 */
import { currentServiceRegion } from '../mobile/navigation/serviceRegionPreference';
import { getPersistentJson } from '../vrs-auth/storage';
import { queueService } from './InterpreterQueueService';
import { getDefaultRequestLanguage } from './interpreterLanguages';
import { recordP2PInterpreterOnCall } from './p2pInterpreterEligibility';
let pendingCallId = null;
let requestSequence = 0;
const listeners = new Set();
/**
 * Set or clear the call with a request out, telling subscribers on change.
 *
 * @param {string|null} callId - The call, or null once the server answered.
 * @returns {void}
 */
function setPendingCallId(callId) {
    if (pendingCallId !== callId) {
        pendingCallId = callId;
        listeners.forEach(listener => listener());
    }
}
/**
 * The active call as stored when it was entered.
 *
 * @returns {Object|null}
 */
function storedCall() {
    return getPersistentJson('vrs_active_call');
}
/**
 * Where and in what language the caller asks: the region their account last
 * picked (vrs_service_region, checked against current grants; null for an
 * undesignated account, when the server decides) and that region's request
 * language — the same pick a queued request uses. Shared by web and native.
 *
 * @returns {{ language: string, serviceRegion: string|null }}
 */
export function p2pInterpreterRequestTarget() {
    const serviceRegion = currentServiceRegion();
    return {
        language: getDefaultRequestLanguage(serviceRegion),
        serviceRegion
    };
}
/** Subscribe to pending changes (for useSyncExternalStore). */
export function subscribeP2PInterpreterRequest(listener) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
/** Whether the current call has a request out; a request from an earlier call does not count. */
export function isP2PInterpreterRequestPending() {
    return Boolean(pendingCallId && storedCall()?.callId === pendingCallId);
}
/**
 * Ask for an interpreter in the caller's region and sign language
 * (p2pInterpreterRequestTarget). Returns false when there is no stored call or
 * one is already out.
 */
export function requestP2PInterpreter() {
    const call = storedCall();
    if (!call?.callId || !call.roomName || pendingCallId === call.callId) {
        return false;
    }
    setPendingCallId(call.callId);
    queueService.sendP2PRequestInterpreter(call.callId, call.roomName, `p2p-native-${Date.now()}-${++requestSequence}`, p2pInterpreterRequestTarget());
    return true;
}
/** Report each verdict for the pending request and clear it. Returns an unsubscribe. */
export function watchP2PInterpreterOutcomes(onOutcome) {
    const onJoined = (data) => {
        setPendingCallId(null);
        onOutcome({
            kind: 'joined',
            interpreterName: data?.interpreterName
        });
    };
    const onUnavailable = (data) => {
        setPendingCallId(null);
        // A revoked region grant withdrew the request
        // (docs/regional-queue-routing.md#grants-and-revocation): say why.
        if (data?.code === 'SERVICE_REGION_REVOKED') {
            onOutcome({ kind: 'failed', code: data.code, message: data.message });
            return;
        }
        onOutcome({ kind: 'unavailable' });
    };
    const onError = (data) => {
        // ALREADY_REQUESTED: an earlier press is still cascading, so stay pending.
        if (data?.operation !== 'p2p_request_interpreter' || data.code === 'ALREADY_REQUESTED') {
            return;
        }
        const callId = pendingCallId || storedCall()?.callId;
        setPendingCallId(null);
        if (data.code === 'P2P_INTERPRETER_PRESENT') {
            recordP2PInterpreterOnCall(callId || undefined);
            onOutcome({ kind: 'present' });
            return;
        }
        onOutcome({
            kind: 'failed',
            code: data.code,
            message: data.message
        });
    };
    queueService.on('p2pInterpreterJoined', onJoined);
    queueService.on('p2pInterpreterUnavailable', onUnavailable);
    queueService.on('error', onError);
    return () => {
        queueService.off('p2pInterpreterJoined', onJoined);
        queueService.off('p2pInterpreterUnavailable', onUnavailable);
        queueService.off('error', onError);
    };
}
